import React, { useContext } from 'react';
import { Box, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReportFormContext } from '../../../contexts/ReportFormContext';

/**
 * Uncontrolled inputs for km/hours — avoids context re-render loop on every keystroke.
 * The `key` prop on this component must be set to `vehicle.id` by the parent so that
 * inputs remount (picking up new defaultValues) when the vehicle changes.
 */
const VehicleUsageFields = ({ vehicle }) => {
    const { t } = useTranslation();
    const { setReport } = useContext(ReportFormContext);

    const isActive = vehicle.checked === true || vehicle.not_from_planning === true;

    if (!isActive) return null;

    const initialKm = vehicle.current_kilometers ? String(vehicle.current_kilometers) : '';
    const initialHours = vehicle.hours_of_use ? String(vehicle.hours_of_use) : '';

    const syncField = (field, rawValue) => {
        const parsed = rawValue && /^\d+$/.test(rawValue) ? parseInt(rawValue) : null;
        setReport((prev) => ({
            ...prev,
            vehicles: prev.vehicles.map((v) =>
                v.id === vehicle.id ? { ...v, [field]: parsed } : v
            ),
        }));
    };

    const handleKmBlur = (e) => syncField('current_kilometers', e.target.value);
    const handleHoursBlur = (e) => syncField('hours_of_use', e.target.value);

    // Enforce numeric-only input without controlling the value
    const handleNumericKeyDown = (e) => {
        const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'];
        if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    };

    return (
        <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, mt: 1 }}>
            <TextField
                label={t('Current kilometers')}
                variant="outlined"
                defaultValue={initialKm}
                onBlur={handleKmBlur}
                onKeyDown={handleNumericKeyDown}
                type="text"
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                size="small"
                fullWidth
            />
            <TextField
                label={t('Hours of use')}
                variant="outlined"
                defaultValue={initialHours}
                onBlur={handleHoursBlur}
                onKeyDown={handleNumericKeyDown}
                type="text"
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                size="small"
                fullWidth
            />
        </Box>
    );
};

export default VehicleUsageFields;